import type {
  CalendarFestival,
  CalendarTodayResponse,
  CalendarMonthResponse,
  CalendarUpcomingResponse,
  CalendarFestivalDetail,
  CalendarEkadashiResponse,
  CalendarPurnimaResponse,
  CalendarAmavasyaResponse,
  CalendarPradoshResponse,
} from "@ishvaram/api-client";
import { apiRequest } from "./_fetch";

export type {
  CalendarFestival,
  CalendarTodayResponse,
  CalendarMonthResponse,
  CalendarUpcomingResponse,
  CalendarFestivalDetail,
  CalendarEkadashiResponse,
  CalendarPurnimaResponse,
  CalendarAmavasyaResponse,
  CalendarPradoshResponse,
};

// The Ekadashi list element carries the full FestivalSummary shape from the
// backend (incl. `month_hi`), which the hand-maintained `CalendarFestival`
// override omits. Derive it from the response so consumers get the real fields.
export type CalendarEkadashiItem = CalendarEkadashiResponse["ekadashis"][number];
// Purnima / Amavasya / Pradosh list elements share the same FestivalSummary shape.
export type CalendarPurnimaItem = CalendarPurnimaResponse["purnimas"][number];
export type CalendarAmavasyaItem = CalendarAmavasyaResponse["amavasyas"][number];
export type CalendarPradoshItem = CalendarPradoshResponse["pradoshas"][number];

// Sankashti Chaturthi list element + response. The backend `GET /calendar/sankashti`
// returns the SAME shape as `/pradosh` ({year, sankashtis:[FestivalSummary], total}).
// `schema.d.ts` is regenerated from the backend OpenAPI spec; until the generator
// run that adds `Schemas["SankashtiResponse"]` lands, the item type is derived from
// the structurally-identical Pradosh item (FestivalSummary) and the response is
// declared locally — same shape, no drift. Swap to the generated alias when present.
export type CalendarSankashtiItem = CalendarPradoshItem;
export interface CalendarSankashtiResponse {
  year: number;
  sankashtis: CalendarSankashtiItem[];
  total: number;
}

// Masik Shivratri (monthly Shiva vrat — Krishna-Paksha Chaturdashi each lunar
// month) + Sankranti (12 solar-ingress days) list elements + responses. The
// backend endpoints return the SAME FestivalSummary item shape as `/pradosh`
// ({year, <items>:[FestivalSummary], total}). Aliased to CalendarPradoshItem
// until the OpenAPI generator emits dedicated schema names — same shape, no
// drift. Swap to the generated alias when present.
export type CalendarMasikShivratriItem = CalendarPradoshItem;
export interface CalendarMasikShivratriResponse {
  year: number;
  masik_shivratris: CalendarMasikShivratriItem[];
  total: number;
}

export type CalendarSankrantiItem = CalendarPradoshItem;
export interface CalendarSankrantiResponse {
  year: number;
  sankrantis: CalendarSankrantiItem[];
  total: number;
}

// Vrat-calendar hub — the backend `GET /calendar/vrat-year` groups every vrat
// type for a year. Each value is a FestivalSummary list keyed by vrat type so
// the hub can list the next date + a link per vrat. Keys mirror the per-vrat
// endpoints; the hub page reads only the next upcoming date from each list.
export interface CalendarVratYearResponse {
  year: number;
  ekadashis: CalendarPradoshItem[];
  amavasyas: CalendarPradoshItem[];
  purnimas: CalendarPradoshItem[];
  pradoshas: CalendarPradoshItem[];
  sankashtis: CalendarPradoshItem[];
  masik_shivratris: CalendarPradoshItem[];
  sankrantis: CalendarPradoshItem[];
}

export async function getCalendarToday(date?: string): Promise<CalendarTodayResponse> {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  const qs = params.toString();
  return apiRequest<CalendarTodayResponse>(`/calendar/today${qs ? `?${qs}` : ""}`);
}

export async function getCalendarMonth(
  year: number,
  month: number
): Promise<CalendarMonthResponse> {
  return apiRequest<CalendarMonthResponse>(`/calendar/month?year=${year}&month=${month}`);
}

export async function getCalendarUpcoming(
  days = 30,
  date?: string
): Promise<CalendarUpcomingResponse> {
  const params = new URLSearchParams({ days: String(days) });
  if (date) params.set("date", date);
  return apiRequest<CalendarUpcomingResponse>(`/calendar/upcoming?${params.toString()}`);
}

export async function getCalendarEkadashi(year = 2026): Promise<CalendarEkadashiResponse> {
  return apiRequest<CalendarEkadashiResponse>(`/calendar/ekadashi?year=${year}`);
}

export async function getCalendarPurnima(year = 2026): Promise<CalendarPurnimaResponse> {
  return apiRequest<CalendarPurnimaResponse>(`/calendar/purnima?year=${year}`);
}

export async function getCalendarAmavasya(year = 2026): Promise<CalendarAmavasyaResponse> {
  return apiRequest<CalendarAmavasyaResponse>(`/calendar/amavasya?year=${year}`);
}

export async function getCalendarPradosh(year = 2026): Promise<CalendarPradoshResponse> {
  return apiRequest<CalendarPradoshResponse>(`/calendar/pradosh?year=${year}`);
}

export async function getCalendarSankashti(year = 2026): Promise<CalendarSankashtiResponse> {
  return apiRequest<CalendarSankashtiResponse>(`/calendar/sankashti?year=${year}`);
}

export async function getCalendarMasikShivratri(
  year = 2026
): Promise<CalendarMasikShivratriResponse> {
  return apiRequest<CalendarMasikShivratriResponse>(`/calendar/masik-shivratri?year=${year}`);
}

export async function getCalendarSankranti(year = 2026): Promise<CalendarSankrantiResponse> {
  return apiRequest<CalendarSankrantiResponse>(`/calendar/sankranti?year=${year}`);
}

export async function getCalendarVratYear(year = 2026): Promise<CalendarVratYearResponse> {
  return apiRequest<CalendarVratYearResponse>(`/calendar/vrat-year?year=${year}`);
}

export async function getCalendarFestival(id: string): Promise<CalendarFestivalDetail> {
  return apiRequest<CalendarFestivalDetail>(`/calendar/festival/${id}`);
}
